import _extends from "@swc/helpers/src/_extends.mjs";
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import AddCoverImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { useMMDispatchActions } from '../../../_hocs/withOwnReduxStore';
import DeviationDumbView from '../../../../DeviationViews/Dumbo';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import { getFolderNameValidationErrorMessage, MAX_TITLE_LENGTH } from '../../../../utils/isValidFolderName';
import { useGallections } from '../../../_hocs/withGallections';
import { setGallectionName, setGallectionDescription, setGallectionPrice } from '../../../actions';
import { useCurrentGallection } from '../../../_hocs/withCurrentGallection';
import { useCurrentSubfolder } from '../../../_hocs/withCurrentSubfolder';
import Textarea from '../../../../Textarea';
import { isGallectionManageable, canChangeGallectionDescription, areGallectionContentsManageable } from '../../../helpers';
import FolderRibbon from '../../../../Primitives/FolderRibbon';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import GallectionCoverSelector from '../../../_partials/GallectionCoverSelector';
import FolderCoverInfo from '../../../../FolderCoverInfo';
import { dollarsToPoints } from '../../../../utils/points';
import yupResolver from '../../../../utils/yupResolver';
import useValidation from './useValidation';
import PriceField from './_partials/PriceField';
import gallectionSidebarStyles from './GallectionSidebar.scss';
const MAX_DESCRIPTION_LENGTH = 1100;
export const GallectionSidebar = ()=>{
    var _currentGallection_premiumFolderData, _currentGallection_premiumFolderData1;
    const { t  } = useTranslation();
    const { gallections  } = useGallections();
    const { currentGallection: currentGallectionFolder  } = useCurrentGallection();
    const { currentSubfolder  } = useCurrentSubfolder();
    const currentGallection = currentSubfolder || currentGallectionFolder;
    const [isShowingMediaSelector, setIsShowingMediaSelector] = useState(false);
    const showMediaSelector = useCallback(()=>setIsShowingMediaSelector(true), [
        setIsShowingMediaSelector
    ]);
    const hideMediaSelector = useCallback(()=>setIsShowingMediaSelector(false), [
        setIsShowingMediaSelector
    ]);
    const [nameErrorMessage, setNameErrorMessage] = useState();
    const [descriptionErrorMessage, setDescriptionErrorMessage] = useState();
    const actions = useMMDispatchActions({
        setGallectionName,
        setGallectionDescription,
        setGallectionPrice
    });
    const existingNames = useMemo(()=>gallections.filter((gallection)=>gallection.folderId !== currentGallection.folderId).map((gallection)=>gallection.name), [
        gallections,
        currentGallection.folderId
    ]);
    const onChangeGallectionName = useCallback((name)=>{
        const message = getFolderNameValidationErrorMessage(name, t, existingNames, currentGallection.type === 'gallery');
        setNameErrorMessage(message);
        actions.setGallectionName({
            folderId: currentGallection.folderId,
            name
        });
    }, [
        currentGallection.folderId,
        currentGallection.type,
        actions,
        existingNames,
        setNameErrorMessage,
        t
    ]);
    const onChangeGallectionDescription = useCallback((description)=>{
        if (description.length >= MAX_DESCRIPTION_LENGTH) {
            setDescriptionErrorMessage(t('mediapopup.gallections.description.error.length'));
        } else {
            setDescriptionErrorMessage(null);
        }
        actions.setGallectionDescription({
            folderId: currentGallection.folderId,
            description
        });
    }, [
        currentGallection.folderId,
        setDescriptionErrorMessage,
        actions,
        t
    ]);
    const onChangeGallectionPrice = useCallback((value, error)=>{
        if (!value || !!error) {
            return;
        }
        actions.setGallectionPrice({
            folderId: currentGallection.folderId,
            pointsPrice: dollarsToPoints(+value)
        });
    }, [
        currentGallection.folderId,
        actions
    ]);
    // TODO: Add proper Fields interface and make all fields use react-hook-form
    const validationSchema = useValidation();
    const formContext = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            price: ((_currentGallection_premiumFolderData = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData === void 0 ? void 0 : _currentGallection_premiumFolderData.dollarPrice.toString()) || ''
        }
    });
    // Data is submitted on change which makes this a bit ugly
    const { handleSubmit , watch  } = formContext;
    const watchPrice = watch('price');
    useEffect(()=>{
        handleSubmit((data)=>{
            onChangeGallectionPrice(data.price);
        })();
    }, [
        handleSubmit,
        onChangeGallectionPrice,
        watchPrice
    ]);
    const renderGallectionCoverBlock = ()=>{
        const gallectionThumb = currentGallection.thumb;
        const { isPremium: isThumbPremium  } = normalizePremiumFolderData(gallectionThumb === null || gallectionThumb === void 0 ? void 0 : gallectionThumb.premiumFolderData);
        const canSetGallectionCover = areGallectionContentsManageable(currentGallection) && (currentGallection === null || currentGallection === void 0 ? void 0 : currentGallection.type) !== 'stash';
        return /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['cover-wrapper']
        }, gallectionThumb ? /*#__PURE__*/ React.createElement(DeviationDumbView, {
            useCoverMode: true,
            width: parseInt(gallectionSidebarStyles['width']),
            height: parseInt(gallectionSidebarStyles[isPaidFolder ? 'cover-height-paid-folder' : 'cover-height']),
            deviation: gallectionThumb
        }) : /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['no-cover']
        }), canSetGallectionCover && !gallectionThumb && /*#__PURE__*/ React.createElement("button", {
            className: gallectionSidebarStyles['cover-button-add'],
            onClick: showMediaSelector
        }, /*#__PURE__*/ React.createElement(AddCoverImageIcon, {
            className: gallectionSidebarStyles['cover-button-icon']
        }), /*#__PURE__*/ React.createElement("br", null), t('mediapopup.setGallectionCover')), canSetGallectionCover && gallectionThumb && /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['cover-buttons-wrapper']
        }, isThumbPremium && /*#__PURE__*/ React.createElement(FolderCoverInfo, null), /*#__PURE__*/ React.createElement(IconButtonWithBackground, {
            size: "small",
            icon: EditIcon,
            className: gallectionSidebarStyles['cover-button-edit'],
            onClick: showMediaSelector
        })));
    };
    const renderSubscribersCount = ()=>{
        var _currentGallection_premiumFolderData, _currentGallection_premiumFolderData1;
        /*#__PURE__*/ return React.createElement("div", {
            className: gallectionSidebarStyles['subscribers-counter']
        }, /*#__PURE__*/ React.createElement("span", null, (_currentGallection_premiumFolderData = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData === void 0 ? void 0 : _currentGallection_premiumFolderData.numSubscribers), ' ', t('common.supporters', {
            count: (_currentGallection_premiumFolderData1 = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData1 === void 0 ? void 0 : _currentGallection_premiumFolderData1.numSubscribers
        }));
    };
    const renderGallectionNameInput = ()=>/*#__PURE__*/ React.createElement(TextInput, {
            id: "gallection-name",
            autoComplete: "off",
            validationResult: nameErrorMessage ? 'error' : undefined,
            details: nameErrorMessage,
            wrapperClassName: gallectionSidebarStyles['gallection-name'],
            label: t('mediapopup.gallections.input.name'),
            value: currentGallection.name,
            onChange: (ev)=>onChangeGallectionName(ev.target.value),
            readOnly: !isGallectionManageable(currentGallection),
            maxLength: MAX_TITLE_LENGTH
        });
    const renderDescriptionTextarea = ()=>{
        let descriptionPlaceholder;
        if (isPaidFolder) {
            descriptionPlaceholder = t('mediapopup.gallections.gallery.paid.description.placeholder');
        } else if (isWatchToAccess) {
            descriptionPlaceholder = t('mediapopup.gallections.gallery.watchers.description.placeholder');
        }
        return /*#__PURE__*/ React.createElement(Textarea, {
            id: "gallection-description",
            wrapperClassName: gallectionSidebarStyles['gallection-description'],
            textareaClassName: gallectionSidebarStyles['gallection-description-textarea'],
            charactersRemainingClassName: gallectionSidebarStyles['gallection-description-chars-remaining'],
            label: t('mediapopup.gallections.input.description'),
            placeholder: descriptionPlaceholder,
            value: currentGallection.description,
            onChange: onChangeGallectionDescription,
            errorMessage: descriptionErrorMessage,
            maxLength: MAX_DESCRIPTION_LENGTH,
            autoSize: true
        });
    };
    const { isPaidFolder , isWatchToAccess  } = normalizePremiumFolderData(currentGallection.premiumFolderData);
    return /*#__PURE__*/ React.createElement("div", {
        className: gallectionSidebarStyles['root']
    }, /*#__PURE__*/ React.createElement(FormProvider, _extends({}, formContext), isShowingMediaSelector && /*#__PURE__*/ React.createElement(GallectionCoverSelector, {
        gallection: currentGallection,
        onClose: hideMediaSelector
    }), /*#__PURE__*/ React.createElement(FolderRibbon, {
        premiumFolderData: currentGallection.premiumFolderData,
        className: gallectionSidebarStyles['premium-badge']
    }), /*#__PURE__*/ React.createElement("div", {
        className: gallectionSidebarStyles['sidebar']
    }, renderGallectionCoverBlock(), isPaidFolder && renderSubscribersCount(), /*#__PURE__*/ React.createElement("div", {
        className: gallectionSidebarStyles['form']
    }, renderGallectionNameInput(), !currentSubfolder && canChangeGallectionDescription(currentGallection) && renderDescriptionTextarea(), ((_currentGallection_premiumFolderData1 = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData1 === void 0 ? void 0 : _currentGallection_premiumFolderData1.type) === 'paid' && /*#__PURE__*/ React.createElement(PriceField, null)))));
};
GallectionSidebar.displayName = 'GallectionSidebar';
export default GallectionSidebar;
